import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { IRootState } from 'Scripts/store';

import { useLazyGetCountriesQuery } from 'Services/countrySelectorApi';
import { dpr } from 'Utils/dpr';
import { BreakPointHooks } from 'Hooks/BreakPointHooks';

import { ICultureViewModel } from 'Types/Litium';
import CtaButton from 'Components/CtaButton';
import Divider from 'Components/Divider';
import Modal from 'Components/Modal';

import CaretDown from 'Icons/caretGsd.svg?react';

const { useGreater } = BreakPointHooks();

const CountrySelector = ({
    currentCulture,
}: {
    currentCulture: ICultureViewModel;
}) => {
    const isGreater = useGreater('lg');
    const [getCountries, { data, isSuccess, isFetching }] =
        useLazyGetCountriesQuery();
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [shouldWarnBeforeChange, setShouldWarnBeforeChange] = useState(false);
    const [linkToMarket, setLinkToMarket] = useState<string>();
    const [forceClose, setForceClose] = useState(false);
    const isCartEmpty = useSelector(
        (state: IRootState) => state?.cart?.miniCartModel.shoppingCart?.isEmpty
    );
    const handleClick = () => {
        getCountries();
    };
    const handleChangeCountry = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        if (data.isLoggedIn || isCartEmpty === false) {
            e.preventDefault();
            setShouldWarnBeforeChange(true);
            setLinkToMarket(e.currentTarget.href);
        }
    };

    useEffect(() => {
        if (isFetching) {
            setForceClose(false);
            setIsOpenModal(false);
            setLinkToMarket(null);
            setShouldWarnBeforeChange(false);
        }
        if (isSuccess) {
            setIsOpenModal(true);
        }
    }, [isSuccess, isFetching]);

    useEffect(() => {
        setIsOpenModal(false);
        setLinkToMarket(null);
        setShouldWarnBeforeChange(false);
        setForceClose(true);
    }, [isGreater]);

    return (
        <>
            <button
                type="button"
                aria-label="Byt land"
                className="group/country flex w-full items-center gap-1 font-montserrat text-sm font-normal uppercase text-black lg:w-auto"
                onClick={handleClick}
                disabled={isFetching}
            >
                {currentCulture.flag?.src && (
                    <img
                        className="h-6 w-6 rounded-full"
                        width="24"
                        height="24"
                        src={`${currentCulture.flag?.src}?width=24&height=24&fit=crop&format=webp`}
                        srcSet={dpr(
                            `${currentCulture.flag?.src}?width=24&height=24&fit=crop&format=webp`
                        )}
                        alt={currentCulture.flag?.altText}
                    />
                )}
                <span className="group-hover/country:underline">
                    {currentCulture.languageCode}
                </span>
                <span className="sr-only">{currentCulture.languageName}</span>
                <CaretDown className="ml-2 h-3.5 w-3.5" />
            </button>
            {isSuccess && data.translations && (
                <>
                    <Modal
                        className="h-fit max-h-[80vh] w-[800px] overflow-auto"
                        onClose={() => {
                            setIsOpenModal(false);
                        }}
                        forceClose={forceClose}
                        isOpened={isOpenModal}
                        ariaCloseButtonTranslation={data.translations.close}
                        backButtonTranslation={data.translations.close}
                        absolutePositionedCross
                    >
                        <h2 className="mb-4 text-center font-montserrat text-h4 leading-tight">
                            {data.translations.chooseCountry}
                        </h2>
                        <Divider margin="my-2" />

                        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:mt-8 lg:grid-cols-4 ">
                            {data.cultures?.map((item, index) => (
                                <li key={`${item.displayName}-${index}`}>
                                    <a
                                        onClick={e => handleChangeCountry(e)}
                                        href={item.currentURL}
                                        className="flex flex-nowrap items-center gap-2 font-roboto text-sm font-light hover:underline"
                                    >
                                        {item.flag?.src ? (
                                            <img
                                                className="h-6 w-6 rounded-full"
                                                width="24"
                                                height="24"
                                                src={`${item.flag?.src}?width=24&height=24&fit=crop&format=webp`}
                                                srcSet={dpr(
                                                    `${item.flag?.src}?width=24&height=24&fit=crop&format=webp`
                                                )}
                                                alt={item.flag?.altText}
                                            />
                                        ) : (
                                            <span className="h-6 w-6 rounded-full" />
                                        )}
                                        <span>{item.displayName}</span>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </Modal>
                    <Modal
                        forceClose={forceClose}
                        showButton={false}
                        className="h-fit max-h-[80vh] w-[800px] overflow-auto"
                        onClose={() => setShouldWarnBeforeChange(false)}
                        ariaCloseButtonTranslation="#Close dialog window for terms and conditions"
                        backButtonTranslation="#Close dialog window for terms and conditions"
                        isOpened={shouldWarnBeforeChange}
                        absolutePositionedCross
                    >
                        <h2 className="mb-4 text-center font-montserrat text-h2">
                            {data.translations.popupHeaderTitle}
                        </h2>
                        <hr className="divider-horizontal" />
                        <div className="mt-8 flex flex-col items-center justify-center">
                            <p className="text-center">
                                {data.isLoggedIn
                                    ? data.translations.userLogout
                                    : data.translations.emptyingShoppingCart}
                            </p>
                            <div className="mt-6 grid grid-cols-1 grid-rows-2 justify-center gap-4 md:grid-cols-2 md:grid-rows-1">
                                <CtaButton
                                    as="a"
                                    className="basis-full md:basis-1/2"
                                    href={linkToMarket}
                                    ctaType="primary"
                                >
                                    {data.translations.changeMarketLinkText}
                                </CtaButton>
                                <CtaButton
                                    ctaType="secondary"
                                    type="button"
                                    className="basis-full md:basis-1/2"
                                    onClick={() =>
                                        setShouldWarnBeforeChange(false)
                                    }
                                >
                                    {
                                        data.translations
                                            .stayOnCurrentMarketLinkText
                                    }
                                </CtaButton>
                            </div>
                        </div>
                    </Modal>
                </>
            )}
        </>
    );
};

export default CountrySelector;
